import { Home, TreePine, Hammer, Squirrel, Bug, BedDouble, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES } from "@/constants/testIds";

const services = [
  {
    id: "households",
    title: "Households",
    code: "S-01",
    description:
      "Comprehensive interior + exterior protection for single-family homes, apartments and multi-units. Roaches, ants, rodents, spiders—handled.",
    Icon: Home,
    accent: false,
  },
  {
    id: "parks-schools",
    title: "Parks & Schools",
    code: "S-02",
    description:
      "Licensed treatment programs for public grounds, playgrounds and school facilities. Child-safe protocols, scheduled visits, full documentation.",
    Icon: TreePine,
    accent: false,
  },
  {
    id: "termites",
    title: "Wood-Destroying Insects",
    code: "S-03",
    description:
      "Termite, carpenter ant and carpenter bee specialists. Real damage assessment, full structural treatment and warranty-backed eradication.",
    Icon: Hammer,
    accent: true,
  },
  {
    id: "wildlife",
    title: "Wildlife Removal",
    code: "S-04",
    description:
      "Raccoons, groundhogs, bats, squirrels. Humane trapping, exclusion repair, attic & crawlspace remediation. Licensed for nuisance wildlife.",
    Icon: Squirrel,
    accent: false,
    image: "https://images.unsplash.com/photo-1727721968052-6e740b7890dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxyYWNjb29uJTIwd2lsZGxpZmV8ZW58MHx8fHwxNzgyNzQ2ODMzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "mosquito",
    title: "Mosquito Control",
    code: "S-05",
    description:
      "Backyard barrier sprays, larvicide programs and season-long protection. Reclaim your yard for cookouts, kids and pets.",
    Icon: Bug,
    accent: false,
  },
  {
    id: "bed-bugs",
    title: "Bed Bugs",
    code: "S-06",
    description:
      "Heat treatment + chemical knockdown protocols. Discreet, thorough and follow-up guaranteed. Hotels, rentals and homes.",
    Icon: BedDouble,
    accent: false,
  },
];

const Services = () => {
  return (
    <section id="services" className="border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-14 items-end">
          <div className="md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
              [ 02 ] — What we handle
            </p>
            <h2 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-4xl md:text-6xl">
              Six categories. <br />
              <span className="text-[#0B2B40]">Zero compromises.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-zinc-700 text-base md:text-lg leading-relaxed border-l-4 border-[#D9480F] pl-5">
              We don't dabble. Every technician is trained on the specific biology, habitat
              and treatment protocol for each pest we list below. If we take the job, we own it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-zinc-900">
          {services.map((s) => {
            const Icon = s.Icon;
            return (
              <article
                key={s.id}
                data-testid={SERVICES.card(s.id)}
                className={`group relative border-r border-b border-zinc-900 p-8 md:p-10 min-h-[320px] flex flex-col justify-between transition-colors ${
                  s.accent ? "bg-[#0B2B40] text-white" : "bg-white text-zinc-900 hover:bg-[#F59E0B]/10"
                }`}
              >
                <div>
                  <div className="flex items-start justify-between mb-8">
                    <div
                      className={`w-14 h-14 flex items-center justify-center border-2 ${
                        s.accent ? "border-white" : "border-zinc-900"
                      }`}
                    >
                      <Icon className="w-7 h-7" strokeWidth={2} />
                    </div>
                    <span
                      className={`font-mono text-[11px] uppercase tracking-[0.2em] ${
                        s.accent ? "text-[#F59E0B]" : "text-zinc-500"
                      }`}
                    >
                      {s.code}
                    </span>
                  </div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl font-semibold tracking-tight leading-tight mb-4">
                    {s.title}
                  </h3>
                  <p
                    className={`text-sm md:text-base leading-relaxed ${
                      s.accent ? "text-zinc-200" : "text-zinc-600"
                    }`}
                  >
                    {s.description}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-8">
                  <Link
                    to={`/services/${s.id}`}
                    className={`font-mono text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2 ${
                      s.accent ? "text-[#F59E0B]" : "text-[#D9480F]"
                    } group-hover:gap-3 transition-all`}
                  >
                    Learn More
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
